/**
 * CopilotMarkdown.jsx - Unified Markdown Renderer for All Co-Pilots
 * 
 * Gold Standard: Executive Chat styling
 * - Tight spacing (mb-2)
 * - Full heading support (h1-h4)
 * - Configurable entity linking
 * - Clean, readable output
 * 
 * UNIFIED PROCESSING FLOW (via processEntityIds):
 * 1. PRE-PROCESS: Before ReactMarkdown parses the content
 *    - Fix markdown spacing (add spaces around **bold**, *italic*)
 *    - Clean markdown formatting from entity IDs
 *    - Strip non-linkable IDs (prod_, comp_, kpi_, etc.)
 * 
 * 2. RENDER: ReactMarkdown parses and renders markdown
 * 
 * 3. POST-PROCESS: On each text node
 *    - Find and render linkable entity IDs as clickable links
 * 
 * @example
 * <CopilotMarkdown 
 *   content={message}
 *   enableEntityLinks={true}
 *   excludeEntityId="feat_123"
 *   className="..."
 * />
 */
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSpacing from './utils/rehypeSpacing';
import { useNavigate } from 'react-router-dom';

// Import EntityLinkedText component for rendering clickable entity links
import EntityLinkedText from './EntityLinkedText';

// Import unified entity ID processor - SINGLE SOURCE OF TRUTH
import {
  processEntityIds,
  hasLinkableEntityIds,
} from './utils/entityIdProcessor';

// =============================================================================
// CHILD PROCESSING COMPONENT
// =============================================================================

/**
 * ProcessChildren - Process text children for entity linking
 * 
 * NOTE: Text has already been pre-processed (spacing fixed, non-linkable IDs stripped)
 * by processEntityIds before ReactMarkdown parsing. This component only needs to:
 * - Find linkable entity IDs and render them as clickable links
 * - Pass through non-entity text as-is (already cleaned)
 * 
 * @param {ReactNode} children - Child nodes from markdown elements
 * @param {boolean} enableEntityLinks - Whether to make entity IDs clickable
 * @param {string} excludeEntityId - Entity ID to suppress (context-aware)
 */
const ProcessChildren = ({ children, enableEntityLinks, excludeEntityId }) => {
  return React.Children.map(children, child => {
    // Only process string children (text nodes)
    if (typeof child === 'string') {
      if (enableEntityLinks && hasLinkableEntityIds(child)) {
        // Render with clickable entity links
        return <EntityLinkedText text={child} excludeEntityId={excludeEntityId} />;
      }
      // Text already cleaned in pre-processing, pass through as-is
      return child;
    }
    // Non-string children (React elements) pass through unchanged
    return child;
  });
};

// =============================================================================
// MARKDOWN COMPONENT RENDERERS
// =============================================================================

/**
 * Create markdown component renderers with entity link support
 * 
 * Each renderer wraps its children with ProcessChildren to handle entity IDs.
 * 
 * @param {boolean} enableEntityLinks - Whether to make entity IDs clickable
 * @param {string} excludeEntityId - Entity ID to exclude from linking (context-aware)
 */
const createComponents = (enableEntityLinks, excludeEntityId, navigate, stripExternalLinks) => ({
  // Headings - Executive style (enabled everywhere now)
  h1: ({ children }) => (
    <h1 className="text-base font-bold mb-2 mt-3 first:mt-0">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold mb-2 mt-3 first:mt-0">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold mb-2 mt-2 first:mt-0">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-medium mb-1 mt-2 first:mt-0">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </h4>
  ),
  
  // Paragraphs - Tight spacing (Executive style)
  p: ({ children }) => (
    <p className="mb-2 last:mb-0">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </p>
  ),
  
  // Lists - Tight spacing
  ul: ({ children }) => (
    <ul className="mb-2 ml-4 list-disc last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 ml-4 list-decimal last:mb-0">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="mb-1 last:mb-0">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </li>
  ),
  
  // Inline formatting
  strong: ({ children }) => (
    <strong className="font-semibold">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </strong>
  ),
  em: ({ children }) => (
    <em className="italic">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </em>
  ),
  
  // Code - Executive style
  // For co-pilot responses, inline code (single backticks) renders as plain text
  // Only block code (triple backticks) gets special formatting
  code: ({ children, inline, className }) => {
    // react-markdown v10: inline === undefined for inline code, inline === false for block code
    // Block code has className (e.g., "language-json") or explicitly inline === false
    const isBlockCode = className || inline === false;
    
    if (isBlockCode) {
      return (
        <code className="block bg-gray-100 dark:bg-gray-800 rounded p-2 text-sm font-mono overflow-x-auto my-2">
          {children}
        </code>
      );
    }
    
    // Inline code - render as plain text (no special styling)
    return <>{children}</>;
  },
  
  // Pre - for code blocks
  pre: ({ children }) => (
    <pre className="my-2">{children}</pre>
  ),
  
  // Blockquote
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-gray-300 dark:border-gray-600 pl-3 italic text-gray-600 dark:text-gray-400 my-2">
      {children}
    </blockquote>
  ),
  
  // Tables - Horizontally scrollable container to prevent overflow
  table: ({ children }) => (
    <div className="overflow-x-auto my-3 max-w-full">
      <table className="min-w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gray-50 dark:bg-gray-800">
      {children}
    </thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
      {children}
    </tbody>
  ),
  tr: ({ children }) => (
    <tr className="border-b border-gray-200 dark:border-gray-700">
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
      <ProcessChildren enableEntityLinks={enableEntityLinks} excludeEntityId={excludeEntityId}>{children}</ProcessChildren>
    </td>
  ),
  
  // Links - intercept /help/ links for client-side navigation, strip external links if flagged
  a: ({ href, children }) => {
    if (href && href.startsWith('/help/') && navigate) {
      return (
        <button
          onClick={(e) => { e.preventDefault(); navigate(href); }}
          className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer inline"
        >
          {children}
        </button>
      );
    }
    // In help copilot context, strip external links to plain text (AI hallucination safety net)
    if (stripExternalLinks && href && !href.startsWith('/')) {
      return <span>{children}</span>;
    }
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        {children}
      </a>
    );
  },
});

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * CopilotMarkdown - Unified ReactMarkdown wrapper with entity linking support
 * 
 * UNIFIED PROCESSING FLOW:
 * 1. PRE-PROCESS (via processEntityIds with stripOnly=true):
 *    - Fix markdown spacing (add spaces around **bold**, *italic*, etc.)
 *    - Clean markdown formatting from entity IDs (**, `, etc.)
 *    - Strip non-linkable entity IDs (prod_, comp_, kpi_, etc.)
 * 
 * 2. RENDER: ReactMarkdown parses and renders the cleaned content
 * 
 * 3. POST-PROCESS (via ProcessChildren on each text node):
 *    - Find linkable entity IDs and render as clickable links
 * 
 * @param {string} content - Markdown content to render
 * @param {boolean} enableEntityLinks - Whether to make entity IDs clickable (default: true)
 * @param {string} excludeEntityId - Entity ID to exclude from linking (e.g., current item being viewed)
 * @param {string} className - Additional CSS classes
 */
const CopilotMarkdown = ({ content, enableEntityLinks = true, excludeEntityId = null, stripExternalLinks = false, className = '' }) => {
  // ==========================================================================
  // HOOKS (must be called unconditionally, before any returns)
  // ==========================================================================
  
  // Navigate for internal /help/ links
  const navigate = useNavigate();

  // Memoize components to maintain referential stability
  const components = useMemo(
    () => createComponents(enableEntityLinks, excludeEntityId, navigate, stripExternalLinks), 
    [enableEntityLinks, excludeEntityId, navigate, stripExternalLinks]
  );

  // ==========================================================================
  // UNIFIED PRE-PROCESSING (Single Source of Truth)
  // ==========================================================================
  // Use processEntityIds with stripOnly=true to run ALL preprocessing steps:
  // 1. cleanEntityIdFormatting - Remove markdown from entity IDs
  // 2. stripNonLinkableIds - Remove prod_, comp_, etc.
  // 3. suppressEntityId - Remove context entity (if provided)
  // 4. fixMarkdownSpacing - Add spaces around **bold**, *italic*
  //
  // This ensures consistent processing across all co-pilots.
  // ==========================================================================
  const preprocessedContent = useMemo(() => {
    if (!content) return '';
    const result = processEntityIds(content, {
      stripOnly: true,  // Don't find entities yet, just clean the text
      suppressEntityId: excludeEntityId,
    });
    return result.text;
  }, [content, excludeEntityId]);
  
  // ==========================================================================
  // EARLY RETURN (after all hooks)
  // ==========================================================================
  if (!content) return null;
  
  return (
    <div className={className}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]} 
        rehypePlugins={[rehypeSpacing]}
        components={components}
      >
        {preprocessedContent}
      </ReactMarkdown>
    </div>
  );
};

export default CopilotMarkdown;
